import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from './firebase'
import { airportTimeZone, lookupAirport } from './airports'
import { zonedTimeToUtc } from './zonedTime'

/* ============================================================================
   Flight lookup.

   Goes through the `lookupFlight` Cloud Function rather than calling
   GoFlightLabs directly — the API key is a bearer JWT on a paid plan, and
   anything in a browser bundle is public.

   The rows come back raw and are resolved here, because the airport timezone
   index already lives on this side for rendering flights. GoFlightLabs returns
   local clock times with no offset and no arrival date, so both ends are
   resolved through their own zone and the arrival is rolled forward when it
   lands after midnight.
   ============================================================================ */

export interface FlightLookupResult {
  flightNumber: string
  airlineCode: string
  originIATA: string
  originCity: string
  destinationIATA: string
  destinationCity: string
  aircraft: string | null
  status: string | null
  departureUTC: Date
  arrivalUTC: Date
  arrivesNextDay: boolean
}

export class FlightLookupError extends Error {}

const functions = getFunctions(app)

/** "ba 286" / "BA-286" → "BA286". Null when it isn't a plausible number. */
export function normaliseFlightNumber(raw: string): string | null {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (cleaned.length < 3 || cleaned.length > 8) return null
  return /^[A-Z]{2,3}\d+$/.test(cleaned) ? cleaned : null
}

export async function lookupFlight(
  rawNumber: string,
  date: string,
): Promise<FlightLookupResult[]> {
  const flightNumber = normaliseFlightNumber(rawNumber)
  if (!flightNumber) {
    throw new FlightLookupError('Enter a flight number like BA286 or NZ7.')
  }

  let rows: Array<Record<string, unknown>>
  try {
    const call = httpsCallable<
      { flightNumber: string; date: string },
      { flightNumber: string; rows: Array<Record<string, unknown>> }
    >(functions, 'lookupFlight')
    const response = await call({ flightNumber, date })
    rows = response.data.rows ?? []
  } catch (caught) {
    const error = caught as { code?: string; message?: string }
    // `not-found` is what an undeployed function returns. Say so plainly
    // rather than blaming the flight number.
    if (error.code === 'functions/not-found' || error.code === 'not-found') {
      throw new FlightLookupError(
        'Flight search isn’t switched on yet. Enter the flight manually.',
      )
    }
    throw new FlightLookupError(error.message ?? 'Flight search failed.')
  }

  const results = rows
    .map((row) => makeResult(row, flightNumber))
    .filter((result): result is FlightLookupResult => result !== null)

  if (results.length === 0) {
    throw new FlightLookupError('No flight found for that number and date.')
  }
  return results
}

/** "San Francisco (SFO)" → { city, iata } */
function splitAirport(value: string): { city: string; iata: string } | null {
  const open = value.lastIndexOf('(')
  const close = value.lastIndexOf(')')
  if (open < 0 || close < 0 || open > close) return null
  const iata = value.slice(open + 1, close).trim().toUpperCase()
  if (iata.length !== 3) return null
  return { city: value.slice(0, open).trim(), iata }
}

/** The API uses an em dash for "not available". */
function nonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed === '—' || trimmed === '-' || trimmed === 'N/A') return null
  return trimmed
}

function makeResult(
  row: Record<string, unknown>,
  flightNumber: string,
): FlightLookupResult | null {
  const dateString = nonEmpty(row.DATE)
  const fromRaw = nonEmpty(row.FROM)
  const toRaw = nonEmpty(row.TO)
  if (!dateString || !fromRaw || !toRaw) return null

  const from = splitAirport(fromRaw)
  const to = splitAirport(toRaw)
  if (!from || !to) return null

  // Prefer scheduled times; fall back to actual if the schedule is blank.
  const departureLocal = nonEmpty(row.STD) ?? nonEmpty(row.ATD)
  const arrivalLocal = nonEmpty(row.STA) ?? nonEmpty(row.ATA)
  if (!departureLocal || !arrivalLocal) return null

  const originZone = airportTimeZone(from.iata) ?? 'UTC'
  const destinationZone = airportTimeZone(to.iata) ?? 'UTC'

  const departureUTC = makeDate(dateString, departureLocal, originZone)
  let arrivalUTC = makeDate(dateString, arrivalLocal, destinationZone)
  if (!departureUTC || !arrivalUTC) return null

  // No arrival date is supplied. If the arrival lands at or before departure
  // the flight crossed midnight — push it forward a day at a time. A scheduled
  // leg never exceeds about two days.
  let guard = 0
  while (arrivalUTC <= departureUTC && guard < 2) {
    arrivalUTC = new Date(arrivalUTC.getTime() + 86_400_000)
    guard += 1
  }

  const airlineCode = /^([A-Z]{2,3})/.exec(flightNumber)?.[1] ?? ''

  return {
    flightNumber,
    airlineCode,
    originIATA: from.iata,
    originCity: from.city || lookupAirport(from.iata)?.city || '',
    destinationIATA: to.iata,
    destinationCity: to.city || lookupAirport(to.iata)?.city || '',
    aircraft: nonEmpty(row.AIRCRAFT),
    status: nonEmpty(row.STATUS),
    departureUTC,
    arrivalUTC,
    arrivesNextDay:
      arrivalUTC.getUTCDate() !== departureUTC.getUTCDate(),
  }
}

const MONTHS: Record<string, number> = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
}

/** Combines "09 Aug 2026" + "7:30 PM" (or "19:30") in a zone into an instant. */
function makeDate(day: string, time: string, timeZone: string): Date | null {
  const dayMatch = /^(\d{1,2})\s+([A-Za-z]{3})[a-z]*\s+(\d{4})$/.exec(day.trim())
  if (!dayMatch) return null
  const month = MONTHS[dayMatch[2].toUpperCase()]
  if (!month) return null

  const timeMatch = /^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/.exec(time.trim())
  if (!timeMatch) return null

  let hour = Number(timeMatch[1])
  const minute = Number(timeMatch[2])
  const meridiem = timeMatch[3]?.toUpperCase()
  if (meridiem === 'PM' && hour < 12) hour += 12
  if (meridiem === 'AM' && hour === 12) hour = 0

  return zonedTimeToUtc(Number(dayMatch[3]), month, Number(dayMatch[1]), hour, minute, timeZone)
}
